import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Circle, CircleAlert, FileText, MessageCircle, RefreshCw, Trash2 } from "lucide-react";
import { ConversationMark } from "../OlliLogo";
import { timeAgo } from "../NotificationsBell";
import { DAY } from "../../lib/format";

/**
 * A chat thread and the chat history list (design: components/assistant/OlliThreadView.tsx).
 *
 * Messages: { id, role: "user" | "olli", text, steps?, facts?, links?, chips?, sources?, error? }
 * (lib/olli.js toThreadMessage). `error` = { message, canRetry } marks a turn
 * that failed; the backend didn't save it, so Retry sends the question again.
 * Chips (follow-up questions) show under the latest answer only.
 * Threads: { id, title, surfaceLabel, updatedAt }.
 */

// **bold** inside a line → <strong>; everything else stays text (React escapes it).
function inline(line) {
  return line.split(/(\*\*[^*\n]+\*\*)/g).map((part, i) => (/^\*\*[^*\n]+\*\*$/.test(part) ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong> : <Fragment key={i}>{part}</Fragment>));
}

/**
 * Olli's text with a small, safe markdown subset: "- ", "* " or "• " lines as a
 * bullet list, **bold**, and line breaks. Older answers were stored with raw
 * markdown; new ones are plain lines, which render as before.
 */
export function OlliText({ text, className = "" }) {
  const blocks = [];
  String(text || "").split("\n").forEach((raw) => {
    const bullet = raw.match(/^\s*[-*•]\s+(.*)$/);
    const last = blocks[blocks.length - 1];
    const line = raw.replace(/^#+\s*/, "");
    if (bullet) {
      if (last?.type === "ul") last.items.push(bullet[1]);
      else blocks.push({ type: "ul", items: [bullet[1]] });
    } else if (last?.type === "p") last.lines.push(line);
    else blocks.push({ type: "p", lines: [line] });
  });
  return (
    <div className={`flex flex-col gap-2 text-small leading-relaxed ${className}`}>
      {blocks.map((block, i) => (block.type === "ul" ? (
        <ul key={i} className="flex flex-col gap-1">
          {block.items.map((item, j) => <li key={j} className="flex gap-2"><span aria-hidden="true" className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-current opacity-60" /><span className="min-w-0">{inline(item)}</span></li>)}
        </ul>
      ) : block.lines.join("\n").trim() ? (
        <p key={i} className="whitespace-pre-wrap [overflow-wrap:anywhere]">{block.lines.map((line, j) => <Fragment key={j}>{j ? "\n" : ""}{inline(line)}</Fragment>)}</p>
      ) : null))}
    </div>
  );
}

export function OlliThreadView({ messages, typing = false, onChip, onRetry, className = "", onOpenAssistant }) {
  const navigate = useNavigate();
  const lastOlli = [...messages].reverse().find((m) => m.role === "olli")?.id;
  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {messages.map((message) => (
        <article key={message.id} className={`animate-fade-in flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
          {message.role === "olli" && (
            <span className="mr-2 mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink">
              <ConversationMark tone="black" className="h-5 w-5" />
            </span>
          )}
          <div className={`min-w-0 max-w-[88%] ${message.role === "user" ? "rounded-lg rounded-br-sm bg-ink px-4 py-3 text-paper" : "flex-1"}`}>
            {message.error ? (
              <div role="alert" className="rounded-lg bg-danger-soft px-4 py-3">
                <p className="flex items-start gap-2 text-small text-danger"><CircleAlert size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" /><span>{message.error.message}</span></p>
                {message.error.canRetry && onRetry && (
                  <button type="button" onClick={() => onRetry(message)} disabled={typing} className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-full bg-paper px-4 text-caption font-semibold text-ink ring-1 ring-line hover:bg-mist disabled:opacity-50">
                    <RefreshCw size={14} strokeWidth={1.75} />
                    Retry
                  </button>
                )}
              </div>
            ) : message.role === "user" ? (
              <p className="whitespace-pre-wrap text-small leading-relaxed [overflow-wrap:anywhere]">{message.text}</p>
            ) : (
              <OlliText text={message.text} />
            )}
            {!!message.steps?.length && (
              <ul className="mt-3 flex flex-col gap-2">
                {message.steps.map((step, index) => (
                  <li key={`${step.label}-${index}`} className={`flex items-start gap-2 text-caption ${step.status === "pending" ? "text-muted" : "text-ink"}`}>
                    {step.status === "done" ? <Check size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-sky-deep" /> : step.status === "failed" ? <CircleAlert size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" /> : <Circle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-muted" />}
                    <span>{step.label}{step.status === "failed" ? <span className="text-danger"> — couldn't finish</span> : null}</span>
                  </li>
                ))}
              </ul>
            )}
            {!!message.facts?.length && (
              <dl className="mt-3 divide-y divide-line">
                {message.facts.map((fact, index) => (
                  <div key={`${fact.label}-${index}`} className="flex items-start justify-between gap-4 py-2 text-caption">
                    <dt className="min-w-0 text-muted [overflow-wrap:anywhere]">{fact.label}</dt>
                    <dd className="min-w-0 text-right font-semibold text-ink [overflow-wrap:anywhere]">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {!!message.sources?.length && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-caption">
                <span className="text-muted">Sources</span>
                {message.sources.map((source, index) => (
                  <button key={`${source.label}-${index}`} type="button" onClick={() => navigate(source.href)} className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full bg-mist px-2.5 py-1 font-medium text-ink hover:bg-sky-soft">
                    <FileText size={12} strokeWidth={1.75} className="shrink-0" />
                    <span className="truncate">{source.label}</span>
                  </button>
                ))}
              </div>
            )}
            {!!message.links?.length && (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
                {message.links.map((item) => (
                  <button key={`${item.href}-${item.label}`} type="button" onClick={() => navigate(item.href, item.state === undefined ? undefined : { state: item.state })} className="text-left text-caption font-semibold text-sky-deep hover:underline">
                    {item.label}
                  </button>
                ))}
              </div>
            )}
            {!!message.chips?.length && message.id === lastOlli && !typing && onChip && (
              <div className="mt-3 flex flex-wrap gap-2">
                {message.chips.map((chip) => (
                  <button key={chip} type="button" onClick={() => onChip(chip)} className="rounded-full bg-mist px-3 py-2 text-left text-caption text-ink transition-colors hover:bg-sky-soft">
                    {chip}
                  </button>
                ))}
              </div>
            )}
          </div>
        </article>
      ))}
      {onOpenAssistant && (
        <button type="button" onClick={onOpenAssistant} className="ml-9 self-start inline-flex items-center gap-1 text-caption font-semibold text-sky-deep hover:underline">
          <MessageCircle size={13} strokeWidth={1.75} />
          Open in Ask Olli
        </button>
      )}
      {typing && (
        <div className="animate-fade-in flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink">
            <ConversationMark tone="black" className="h-5 w-5" />
          </span>
          {/* Typing dots, not a second logo: the avatar beside the bubble is already the mark. */}
          <div className="flex h-9 items-center gap-1 rounded-lg bg-mist px-3" aria-label="Olli is typing">
            {[0, 150, 300].map((delay) => (
              <span key={delay} className="h-1.5 w-1.5 animate-olli-pulse rounded-full bg-ink/60" style={{ animationDelay: `${delay}ms` }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function historyGroup(thread, now) {
  const age = now - thread.updatedAt;
  if (age < DAY) return "Today";
  if (age < 2 * DAY) return "Yesterday";
  return "Earlier";
}

/** `onDelete(id)` deletes a chat; resolves when done. */
export function ThreadHistory({ threads, onSelect, onDelete, limit, onViewAll, className = "" }) {
  const [confirmId, setConfirmId] = useState(null);
  const list = threads.slice(0, limit);
  const now = Date.now();
  const grouped = ["Today", "Yesterday", "Earlier"]
    .map((label) => ({ label, rows: list.filter((thread) => historyGroup(thread, now) === label) }))
    .filter((group) => group.rows.length);
  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {grouped.map((group) => (
        <section key={group.label}>
          <h3 className="mb-2 text-caption font-semibold text-muted">{group.label}</h3>
          <ul className="flex flex-col gap-1">
            {group.rows.map((thread) => (
              <li key={thread.id} className="flex min-w-0 items-center gap-2 rounded-lg px-2 py-2 hover:bg-mist">
                <button type="button" onClick={() => onSelect(thread.id)} className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-small font-medium text-ink">{thread.title}</span>
                  <span className="mt-0.5 flex items-center gap-2 text-caption text-muted">
                    <span>{thread.surfaceLabel}</span>
                    <span aria-hidden="true">·</span>
                    <span>{timeAgo(thread.updatedAt)}</span>
                  </span>
                </button>
                {confirmId === thread.id ? (
                  <span className="flex shrink-0 items-center gap-1">
                    <span className="text-caption text-muted">Delete chat?</span>
                    <button
                      type="button"
                      onClick={() => {
                        onDelete?.(thread.id);
                        setConfirmId(null);
                      }}
                      className="rounded-full bg-danger-soft px-2 py-1 text-caption font-semibold text-danger"
                    >
                      Delete
                    </button>
                    <button type="button" onClick={() => setConfirmId(null)} className="rounded-full px-2 py-1 text-caption text-muted hover:bg-paper">
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button type="button" aria-label={`Delete ${thread.title}`} title="Delete chat" onClick={() => setConfirmId(thread.id)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted hover:bg-paper hover:text-danger">
                    <Trash2 size={15} strokeWidth={1.75} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {!list.length && <p className="py-6 text-center text-small text-muted">No chats yet.</p>}
      {onViewAll && threads.length > (limit ?? Infinity) && (
        <button type="button" onClick={onViewAll} className="self-start text-small font-semibold text-sky-deep hover:underline">
          View all chats
        </button>
      )}
    </div>
  );
}
